import React, { useState, useRef, useEffect } from "react";
import { Search } from "lucide-react";

function TicketList({
  selectedTicket,
  onTicketSelect,
  onStatusChange,
  conversations = [],
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isExpanded, setIsExpanded] = useState(true);
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState([
    "In Progress",
    "Resolved",
  ]);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowStatusFilter(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Status options for filtering
  const statusOptions = [
    { value: "In Progress", label: "In Progress", color: "#f59e0b" },
    { value: "Resolved", label: "Resolved", color: "#166534" },
  ];

  // ✅ Utility for time formatting
  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return "Just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min${minutes > 1 ? "s" : ""} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? "s" : ""} ago`;
  };

  // ✅ Avatar and first letter helper
  const getAvatarClass = (name) => {
    const firstLetter = name?.charAt(0)?.toLowerCase() || "d";
    return `avatar-${firstLetter}`;
  };

  const getFirstLetter = (name) => name?.charAt(0)?.toUpperCase() || "D";

  const filteredTickets = Array.isArray(conversations)
    ? conversations.filter((ticket) => {
        const matchesSearch =
          ticket.clinicName
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          ticket.lastMessage?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStatus = selectedStatuses.includes(ticket.status);
        return matchesSearch && matchesStatus;
      })
    : [];

  const handleStatusToggle = (status) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const TicketListStyles = `
    .ticket-list-container {
      height: 100%;
      display: flex;
      flex-direction: column;
      background: white;
      border-right: 1px solid #e5e5e5;
      overflow: hidden;
    }

    .ticket-list-header {
      padding: 16px;
      border-bottom: 1px solid #e5e5e5;
      background-color: white;
      flex-shrink: 0;
    }

    .header-title {
      margin: 0 0 16px 0;
      font-size: 16px;
      font-weight: 600;
      color: #1f2937;
    }

    .search-filter-container {
      display: flex;
      gap: 8px;
      margin-bottom: 16px;
    }

    .search-container {
      position: relative;
      flex: 1;
    }

    .search-input {
      width: 100%;
      position: relative;
      padding: 8px 12px 8px 36px;
      border: 1px solid #d1d5db;
      border-radius: 6px;
      font-size: 14px;
      color: #374151;
      background: white;
      outline: none;
      box-sizing: border-box;
    }

    .search-input::placeholder {
      color: #9ca3af;
    }

    .search-input:focus {
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    .search-icon {
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      color: #9ca3af;
      font-size: 14px;
    }

    .filter-dropdown-container {
      position: relative;
    }

    .filter-btn {
      width: 41px;
      height: 43px;
      border: 1px solid #d1d5db;
      background: white;
      cursor: pointer;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #6b7280;
      transition: all 0.15s ease;
    }

    .filter-btn:hover {
      background-color: #f9fafb;
      border-color: #9ca3af;
    }

    .filter-btn.active {
      background-color: #eff6ff;
      border-color: #3b82f6;
      color: #3b82f6;
    }

    .filter-dropdown {
      position: absolute;
      top: 100%;
      right: 0;
      margin-top: 4px;
      background: white;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
      z-index: 1000;
      min-width: 200px;
      overflow: hidden;
    }

    .filter-dropdown-header {
      padding: 12px 16px;
      border-bottom: 1px solid #f3f4f6;
      background: #f8f9fa;
    }

    .filter-dropdown-title {
      font-size: 13px;
      font-weight: 600;
      color: #374151;
      margin: 0;
    }

    .filter-options {
      padding: 8px 0;
      max-height: 200px;
      overflow-y: auto;
    }

    .filter-option {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 16px;
      cursor: pointer;
      transition: background-color 0.15s ease;
    }

    .filter-option:hover {
      background-color: #f9fafb;
    }

    .status-indicator {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .filter-option-label {
      font-size: 14px;
      color: #374151;
      flex: 1;
    }

    .filter-checkbox {
      width: 16px;
      height: 16px;
      border: 2px solid #d1d5db;
      border-radius: 3px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.15s ease;
    }

    .filter-checkbox.checked {
      background: #3b82f6;
      border-color: #3b82f6;
      color: white;
    }

    .section-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 0;
      cursor: pointer;
    }

    .section-title {
      font-size: 14px;
      font-weight: 500;
      color: #374151;
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .ticket-count {
      background: #e5e7eb;
      color: #6b7280;
      padding: 2px 6px;
      border-radius: 10px;
      font-size: 12px;
      font-weight: 500;
    }

    .expand-icon {
      color: #6b7280;
      font-size: 12px;
      transform: ${isExpanded ? "rotate(90deg)" : "rotate(0deg)"};
      transition: transform 0.2s ease;
    }

    .tickets-container {
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      height: 0;
    }

    .tickets-container {
      -ms-overflow-style: none;
      scrollbar-width: none;
    }

    .tickets-container::-webkit-scrollbar {
      display: none;
    }

    .ticket-item {
      padding: 12px 16px;
      border-bottom: 1px solid #f3f4f6;
      cursor: pointer;
      transition: all 0.15s ease;
      background: white;
    }

    .ticket-item:hover {
      background-color: #f9fafb;
    }

    .ticket-item.selected {
      background-color: #eff6ff;
      border-right: 3px solid #3b82f6;
    }

    .ticket-header {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      margin-bottom: 8px;
    }

    .ticket-avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: 600;
      font-size: 13px;
      flex-shrink: 0;
    }

    /* Avatar colors for all 15 users - Single letter based */
    .avatar-d { background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); }
    .avatar-j { background: linear-gradient(135deg, #ec4899 0%, #be185d 100%); }
    .avatar-b { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); }
    .avatar-r { background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%); }
    .avatar-k { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
    .avatar-s { background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); }
    .avatar-m { background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%); }
    .avatar-e { background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); }
    .avatar-l { background: linear-gradient(135deg, #84cc16 0%, #65a30d 100%); }
    .avatar-a { background: linear-gradient(135deg, #ec4899 0%, #be185d 100%); }
    .avatar-c { background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%); }

    .ticket-content {
      flex: 1;
      min-width: 0;
    }

    .ticket-name-time {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 4px;
    }

    .ticket-name {
      font-size: 14px;
      font-weight: 600;
      color: #1f2937;
    }

    .ticket-time {
      font-size: 12px;
      color: #9ca3af;
      white-space: nowrap;
    }

    .ticket-message {
      font-size: 13px;
      color: #6b7280;
      line-height: 1.4;
      margin-bottom: 8px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .ticket-badges {
      display: flex;
      gap: 6px;
      align-items: center;
      flex-wrap: wrap;
    }

    .status-badge {
      padding: 3px 8px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 500;
      text-transform: capitalize;
    }

    .status-open {
      color: #1573b8;
      background-color: #dcedfc;
    }

    .status-in-progress {
      background-color: #fef3c7;
      color: #f59e0b;
    }

    .status-resolved {
      background-color: #dcfce7;
      color: #166534;
    }

    .department-badge {
      background-color: #f3f4f6;
      color: #6b7280;
      padding: 2px 6px;
      border-radius: 10px;
      font-size: 10px;
      font-weight: 500;
    }
  `;

  return (
    <>
      <style>{TicketListStyles}</style>
      <div className="ticket-list-container">
        <div className="ticket-list-header">
          <h2 className="header-title">Recent Tickets</h2>

          <div className="search-filter-container">
            <div className="search-container flex items-center">
              <Search
                className="w-5 h-5 text-gray-500 mr-2"
                style={{
                  position: "absolute",
                  zIndex: "1",
                  left: "8px",
                  color: "grey",
                }}
              />
              <input
                type="text"
                className="search-input"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="filter-dropdown-container" ref={dropdownRef}>
              <button
                className={`filter-btn ${showStatusFilter ? "active" : ""}`}
                onClick={() => setShowStatusFilter(!showStatusFilter)}
              >
                <span>☰</span>
              </button>

              {showStatusFilter && (
                <div className="filter-dropdown">
                  <div className="filter-dropdown-header">
                    <h4 className="filter-dropdown-title">Status</h4>
                  </div>
                  <div className="filter-options">
                    {statusOptions.map((option) => (
                      <div
                        key={option.value}
                        className="filter-option"
                        onClick={() => handleStatusToggle(option.value)}
                      >
                        <div
                          className="status-indicator"
                          style={{ backgroundColor: option.color }}
                        ></div>
                        <span className="filter-option-label">
                          {option.label}
                        </span>
                        <div
                          className={`filter-checkbox ${
                            selectedStatuses.includes(option.value)
                              ? "checked"
                              : ""
                          }`}
                        >
                          {selectedStatuses.includes(option.value) && "✓"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div
            className="section-header"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <div className="section-title">
              My Open Tickets
              <span className="ticket-count">({filteredTickets.length})</span>
            </div>
            <span className="expand-icon">▶</span>
          </div>
        </div>

        {isExpanded && (
          <div className="tickets-container">
            {filteredTickets.map((ticket) => (
              <div
                key={ticket.conversationID}
                className={`ticket-item ${
                  selectedTicket?.conversationID === ticket.conversationID
                    ? "selected"
                    : ""
                }`}
                onClick={() => onTicketSelect(ticket)}
              >
                <div className="ticket-header">
                  <div
                    className={`ticket-avatar ${getAvatarClass(
                      ticket.clinicName
                    )}`}
                  >
                    {getFirstLetter(ticket.clinicName)}
                  </div>

                  <div className="ticket-content">
                    <div className="ticket-name-time">
                      <span className="ticket-name">{ticket.clinicName}</span>
                      <span className="ticket-time">
                        {getTimeAgo(ticket.createdAt)}
                      </span>
                    </div>

                    <div className="ticket-message">
                      {ticket.lastMessage || "No messages yet"}
                    </div>

                    <div className="ticket-badges">
                      <span
                        className={`status-badge status-${ticket.status
                          .toLowerCase()
                          .replace(" ", "-")}`}
                      >
                        {ticket.status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default TicketList;
